import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ActivateResellerApplicationInput,
  ActivateResellerApplicationResponse,
  CountersignDocumentKind,
  ListResellerApplicationsResponse,
  RejectResellerApplicationInput,
  ResellerApplicationDocumentKind,
  ResellerApplicationStatusFilter,
  ShowResellerApplicationResponse,
} from "@xop/dashboard-shared";

import { api, apiBlob, apiUploadForm } from "@/lib/api";
import { saveBlob } from "@/lib/download";

/**
 * The data layer for CRM ▸ Reseller Management ▸ Reseller Applications
 * (moved out of System Admin on 2026-09-12; the BFF prefix is unchanged).
 * Every call goes to the dashboard server's BFF (`/api/admin/reseller-applications/*`),
 * which mints the CRM token — the page never talks to crm-api directly and
 * never sees an ActiveStorage URL.
 *
 * Keys are exported so a mutation can only ever invalidate a key a query
 * reads (same discipline as `reseller-accounts/useResellerMemberMutations.ts`).
 */

const ROOT = "/api/admin/reseller-applications";

export function resellerApplicationsKey(filter: ResellerApplicationStatusFilter): readonly (string | number)[] {
  return ["admin", "reseller-applications", filter];
}

export function resellerApplicationKey(id: number | null): readonly (string | number)[] {
  return ["admin", "reseller-application", id ?? 0];
}

export function resellerOnboardingKey(resellerId: number): readonly (string | number)[] {
  return ["admin", "reseller-onboarding", resellerId];
}

export function useResellerApplications(
  filter: ResellerApplicationStatusFilter,
  options: { retry?: boolean; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: resellerApplicationsKey(filter),
    queryFn: () => api<ListResellerApplicationsResponse>(`${ROOT}?status=${filter}`),
    // The queue tab keeps TanStack's default retries (a blip should not
    // empty the queue). A caller that renders nothing on error — the tab
    // badge on Reseller Management, the profile's Onboarding card — passes
    // `retry: false`: for an actor the BFF refuses the 403 is an answer, not
    // a flake, and three more of them buy nothing.
    ...(options.retry === undefined ? {} : { retry: options.retry }),
    // `enabled: false` for an actor WITHOUT `reseller_applications:access`,
    // so the badge never even asks — a 403 a page expects is still noise.
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

export function useResellerApplication(id: number | null) {
  return useQuery({
    queryKey: resellerApplicationKey(id),
    queryFn: () => api<ShowResellerApplicationResponse>(`${ROOT}/${id}`),
    enabled: id !== null,
  });
}

/** The reseller-profile card's question: did this reseller come through
 * onboarding? `retry: false` because a 403 (an actor without
 * `crm:resellers`) is an answer — "not for you" — not a flake. */
export function useResellerOnboarding(resellerId: number) {
  return useQuery({
    queryKey: resellerOnboardingKey(resellerId),
    queryFn: () => api<ListResellerApplicationsResponse>(`${ROOT}?reseller_id=${resellerId}`),
    retry: false,
  });
}

export function useResellerApplicationMutations(id: number) {
  const qc = useQueryClient();
  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ["admin", "reseller-applications"] });
    await qc.invalidateQueries({ queryKey: resellerApplicationKey(id) });
    // A decided application (countersign/activate/reject) can flip what a
    // mounted CRM reseller-profile card's `useResellerOnboarding` shows, but
    // that hook is keyed by resellerId, not by this application's id — so
    // invalidate the whole onboarding root rather than one reseller's key.
    await qc.invalidateQueries({ queryKey: ["admin", "reseller-onboarding"] });
    // A countersign Replace swaps the bytes behind an already-fetched,
    // 5-minute-stale inline-viewer query (DocumentsCard's
    // `["admin", "reseller-application-document", id, kind]`) — without this,
    // reopening View after Replace shows the old PDF until the staleTime lapses.
    await qc.invalidateQueries({ queryKey: ["admin", "reseller-application-document", id] });
  };

  const countersign = useMutation({
    mutationFn: async (files: Partial<Record<CountersignDocumentKind, File>>) => {
      // The browser builds the multipart body (boundary included) and the
      // BFF forwards it byte-for-byte. Field names are crm-api's attachment
      // names, so the upload lands on the right slot without a re-map.
      const form = new FormData();
      if (files.countersigned_agreement) {
        form.append("countersigned_agreement", files.countersigned_agreement, files.countersigned_agreement.name);
      }
      if (files.countersigned_nda) {
        form.append("countersigned_nda", files.countersigned_nda, files.countersigned_nda.name);
      }
      return apiUploadForm<ShowResellerApplicationResponse>(`${ROOT}/${id}/countersign`, form);
    },
    onSuccess: invalidate,
  });

  const activate = useMutation({
    mutationFn: async (input: ActivateResellerApplicationInput) =>
      api<ActivateResellerApplicationResponse>(`${ROOT}/${id}/activate`, { method: "POST", body: input }),
    onSuccess: invalidate,
  });

  const reject = useMutation({
    mutationFn: async (input: RejectResellerApplicationInput) =>
      api<ShowResellerApplicationResponse>(`${ROOT}/${id}/reject`, { method: "POST", body: input }),
    onSuccess: invalidate,
  });

  return { countersign, activate, reject };
}

export function applicationDocumentPath(id: number, kind: ResellerApplicationDocumentKind): string {
  return `${ROOT}/${id}/documents/${kind}`;
}

/** Bytes for the inline `PdfReader` — a bearer-authenticated fetch, since an
 * `<iframe src>` could carry neither the bearer nor survive the desktop shell. */
export async function fetchApplicationDocumentBytes(
  id: number,
  kind: ResellerApplicationDocumentKind,
): Promise<ArrayBuffer> {
  const blob = await apiBlob(applicationDocumentPath(id, kind));
  return blob.arrayBuffer();
}

export async function downloadApplicationDocument(
  id: number,
  kind: ResellerApplicationDocumentKind,
  filename: string,
): Promise<void> {
  const blob = await apiBlob(applicationDocumentPath(id, kind));
  await saveBlob(filename, blob);
}
